// Contrat de la lecture assistée des documents (lot L7, D-281, D-288, D-289), reprise de l'API d'Oria
// (Infinity Document ia : docs/openapi-oria.json, backend/src/ida/api/v1.py, services.py ; lus avant
// adaptation, aucun code Python copié). Assistance désactivée par défaut ; simulateur local distinct du mode
// réel ; aucun document transmis sans autorisation explicite et destination configurée ; chaque proposition
// est relue par un agent et ne modifie seule ni un dossier ni une décision.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');

export const MODES_ASSISTANCE = {
  desactive: 'Désactivée',
  simulateur: 'Simulateur local (aucune transmission)',
  reel: 'Raccordement réel à Oria',
} as const;
export type ModeAssistance = keyof typeof MODES_ASSISTANCE;

/** Machine d'états d'une lecture (adaptée de la machine des travaux d'Oria). */
export const ETATS_LECTURE = {
  autorisee: 'Transmission autorisée',
  deposee: 'Pièce déposée',
  en_analyse: 'Analyse en cours',
  a_relire: 'Propositions à relire',
  relue: 'Relue',
  echec: 'Échec (instruction manuelle possible)',
  annulee: 'Annulée',
} as const;
export type EtatLecture = keyof typeof ETATS_LECTURE;

export const TRANSITIONS_LECTURE: Record<EtatLecture, readonly EtatLecture[]> = {
  autorisee: ['deposee', 'echec', 'annulee'],
  deposee: ['en_analyse', 'echec', 'annulee'],
  en_analyse: ['a_relire', 'echec', 'annulee'],
  a_relire: ['relue', 'annulee'],
  echec: ['autorisee', 'annulee'],
  relue: [],
  annulee: [],
};

export const REVUES_PROPOSITION = {
  a_relire: 'À relire',
  confirmee: 'Confirmée',
  corrigee: 'Corrigée',
  rejetee: 'Rejetée',
} as const;
export type RevueProposition = keyof typeof REVUES_PROPOSITION;

export const FINALITES_LECTURE = {
  classement: "Classement d'une pièce reçue",
  instruction: "Aide à l'instruction d'un dossier",
} as const;
export type FinaliteLecture = keyof typeof FINALITES_LECTURE;

export const schemaConfigurationAssistance = z
  .strictObject({
    mode: z.enum(['desactive', 'simulateur', 'reel']),
    /** Destination autorisée côté Oria (destination_id), exigée en mode réel. */
    destination: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9_-]{2,64}$/, 'Code de destination attendu')
      .optional(),
    /** Durée de conservation demandée au prestataire, en jours, exigée en mode réel. */
    conservation_jours: z.number().int().min(1).max(365).optional(),
    motif: z.string().trim().min(3).max(500),
  })
  .refine(
    (c) => c.mode !== 'reel' || (Boolean(c.destination) && c.conservation_jours !== undefined),
    {
      message: 'Le mode réel exige une destination et une durée de conservation',
      path: ['destination'],
    },
  );
export type ConfigurationAssistanceSaisie = z.infer<typeof schemaConfigurationAssistance>;

export const schemaDemandeLecture = z.strictObject({
  document_id: uuid,
  finalite: z.enum(['classement', 'instruction']),
  /** Autorisation explicite de transmettre la pièce à la destination affichée. */
  autorisation_transmission: z.literal(true, {
    message: 'La transmission doit être explicitement autorisée',
  }),
});
export type DemandeLecture = z.infer<typeof schemaDemandeLecture>;

export const schemaRevueProposition = z
  .strictObject({
    decision: z.enum(['confirmee', 'corrigee', 'rejetee']),
    valeur: z.string().trim().max(500).optional(),
  })
  .refine((r) => r.decision !== 'corrigee' || Boolean(r.valeur), {
    message: 'Une correction donne la valeur lue',
    path: ['valeur'],
  });
export type RevuePropositionSaisie = z.infer<typeof schemaRevueProposition>;

export interface ConfigurationAssistance {
  mode: ModeAssistance;
  destination: string | null;
  conservation_jours: number | null;
  motif: string | null;
  modifie_le: string | null;
  /** Le serveur dispose d'une adresse autorisée et d'un compte de service (sans jamais les exposer). */
  raccordement_reel_disponible: boolean;
}

export interface PropositionLecture {
  id: string;
  champ: string;
  etat_source: 'present' | 'absent' | 'ambiguous';
  valeur_proposee: string | null;
  candidats: string[];
  preuves: { page: number | null; methode: string | null }[];
  revue: RevueProposition;
  valeur_retenue: string | null;
  relue_par: string | null;
  relue_le: string | null;
}

export interface LectureAssistee {
  id: string;
  document: { id: string; titre: string };
  finalite: FinaliteLecture;
  mode: 'simulateur' | 'reel';
  destination: string | null;
  etat: EtatLecture;
  erreur: string | null;
  tentatives: number;
  demandee_par: string;
  cree_le: string;
  modifie_le: string;
  propositions: PropositionLecture[];
}
